import React, { useEffect, useState } from 'react';
import Moment from 'moment';
import { Grid } from '@uhg-abyss/web/ui/Grid';
import { styled } from '@uhg-abyss/web/tools/styled';
import { useUser } from '@src/state/user/hooks';
import { Alert } from '@uhg-abyss/web/ui/Alert';
import { useTinRegistration } from '@src/state/tinRegistration';
import { useTermsOfUse } from '@src/state/TermsOfUse';
import messages from '@src/common/messages.json';
import { PageLayout } from '@src/common/PageLayout';
import { event as adobeTaggingEvent } from '@uhg-abyss/web/tools/event';
import { config } from '@uhg-abyss/web/tools/config';
import { Link } from '@uhg-abyss/web/ui/Link';
import { useRouter } from '@uhg-abyss/web/hooks/useRouter';
import { Loading } from '@src/common/Loading';
import { storage } from '@uhg-abyss/web/tools/storage';
import { useForm as useAbyssForm } from '@uhg-abyss/web/hooks/useForm';
import { TinRegistration } from './TinRegistration';
import { TinRegistrationStatus } from './TinRegistrationStatus';
import { UserBlockedModal } from './UserBlockedModel/UserBlockedModal';
import { TermsOfUseModel } from './TermsOfUse/TermsOfUseModel';
import { SavedRegistration } from './SavedRegistration';

const PatientName = styled('h1', {
  fontSize: '40px',
  fontWeight: 600,
  paddingTop: '20px',
  textAlign: 'center',
  fontFamily: '$heading',
  color: '$primary1',
  width: '1272px',
  height: '43.2px',
  maxWidth: '100%',
  lineHeight: '43.2px',
  margin: 'auto',
});

const Heading = styled('p', {
  marginLeft: '150px',
  marginRight: '150px',
  fontWeight: 'normal',
  paddingTop: '30px',
  paddingBottom: '50px',
  textAlign: 'center',
  fontFamily: '$primary',
  height: '44.8px',
  fontSize: '16px',
  color: 'rgb(51, 51, 51)',
  lineheight: '22.4px',
});

const AlertDisplay = styled('div', {
  marginBottom: '2%',
  marginTop: '-16px',
});

const NoBottomMarginText = styled('p', {
  marginBottom: '0px',
});

export const Landing = () => {
  const abyssForm = useAbyssForm();
  const { firstName, uuid } = useUser.state();
  const isSuperUser = storage.local.get(uuid);
  const { retrieveDraft, toggleReturn } = useTinRegistration.actions();
  const { updateTerms, validateTerms } = useTermsOfUse.actions();
  const { retrieveDraftSaga } = useTinRegistration.state();

  const [errorRes, setErrorRes] = useState({
    error: {
      message: '',
    },
  });
  const [isAlertBanner, setIsAlertBanner] = useState(false);
  const [isTinRegError, setTinRegError] = useState(false);
  const [isTinRegStatusError, setTinRegStatusError] = useState(false);
  const { setDocumentId } = useTinRegistration.actions();
  const [isSessionDraft, setSessionDraft] = useState(false);
  const [tinDraft, setTinDraft] = useState([]);
  const [isUserBlocked, setIsUserBlocked] = useState(false);
  const [isTermsOfUse, setTermsOfUse] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState(0);
  const [searchFilter, setFilter] = useState();

  useEffect(() => {
    if (errorRes.error) {
      const { statusCode, faultCode } = errorRes.error;
      if (
        (statusCode === 500 && faultCode === 'NEASEE') ||
        statusCode === 409
      ) {
        setIsAlertBanner(true);
      } else if (faultCode === 'EPTSUB') {
        const ms = Date.parse(errorRes.error.timestamp) - Date.now();
        setTimeRemaining(ms);
        setIsUserBlocked(true);
      }
    }
  }, [errorRes]);

  const formatstartDate = (createdDate) => {
    const year = createdDate.slice(0, 4);
    const month = createdDate.slice(5, 7);
    const day = createdDate.slice(8, 10);
    return `${month}/${day}/${year}`;
  };

  const getExpirationDate = (createdDate) => {
    const res = new Date(createdDate);
    res.setDate(res.getDate() + 10);
    const date = res.getDate();
    const month = res.getMonth() + 1;
    const year = res.getFullYear();
    return `${month < 10 ? `0${month}` : `${month}`}/${
      date < 10 ? `0${date}` : `${date}`
    }/${year}`;
  };

  const getExpirsDate = (createdDate) => {
    const res = new Date(createdDate);
    res.setDate(res.getDate() + 10);
    const currentDate = Moment(new Date(), 'DD-MM-YYYY');
    const expiryDate = Moment(res, 'DD-MM-YYYY');
    return expiryDate.diff(currentDate, 'days');
  };

  const getFormatedDate = (payload) => {
    const savedRegistration = [];
    if (payload && payload.length) {
      payload.forEach((element) => {
        const startDate = element.createdDt
          ? formatstartDate(element.createdDt)
          : null;
        const expiryDate = getExpirationDate(startDate);
        const expiryOn = getExpirsDate(startDate);
        savedRegistration.push({
          name: element.w9Details.name,
          billingTin: element.billingTin,
          startDate,
          attestationDetails: element.attestationDetails,
          address: element.address,
          createdDt: element.createdDt,
          caseStatus: element.caseStatus,
          emailID: element.emailID,
          ipAddress: element.ipAddress,
          optumId: element.optumId,
          practiceInformation: element.practiceInformation,
          requestId: element.requestId,
          trackingNumber: element.trackingNumber,
          uuid: element.uuid,
          w9Details: element.w9Details,
          expiryDate,
          expiryOn,
        });
      });
    }
    return savedRegistration;
  };

  const userInfo = useUser.state();
  useEffect(() => {
    setFilter(null);
    const pageName = 'epvsecure:home';
    const billingTin = '';
    const siteSectionLevel = {
      siteSectionLevel1: 'home',
      siteSectionLevel2: '',
      siteSectionLevel3: '',
      siteSectionLevel4: '',
      siteSectionLevel5: '',
    };
    adobeTaggingEvent('ON_PAGE_LOAD', {
      userInfo,
      pageName,
      billingTin,
      siteSectionLevel,
    });

    validateTerms(
      { uuid },
      (res) => {
        if (res && res.accepted) {
          setTermsOfUse(false);
        } else {
          setTermsOfUse(true);
        }
      },
      (error) => {}
    );

    if (!isSuperUser) {
      retrieveDraft(
        {},
        (res) => {
          if (res && res.length) {
            setSessionDraft(true);
          } else {
            setSessionDraft(false);
          }
          const result = getFormatedDate(res);
          setTinDraft(result);
        },
        (error) => {}
      );
    }
  }, []);

  const messageDescription =
    errorRes.error.faultCode === 'TSTCAL' ? (
      <NoBottomMarginText>
        This TIN is already registered with UnitedHealthcare. If you would like
        to access the UnitedHealthcare Provider Portal,{' '}
        <Link
          id="provider-link"
          href={config('NEW_USER_REGISTRATION_URL')}
          target="_blank"
          rel="noopener noreferrer"
        >
          click here
        </Link>
        . (T-001)
      </NoBottomMarginText>
    ) : (
      errorRes.error.message
    );

  const getMessage = () => {
    if (isTinRegError) {
      return messages.TIN_STATUS_FAILED;
    }
    if (isTinRegStatusError) {
      return messages.TIN_REGISTRATION_STATUS_FAILED;
    }
    return messageDescription;
  };

  const router = useRouter();
  const { setTrackingNumber } = useTinRegistration.actions();

  const populateLandingPage = (billingTin, requestId) => {
    const model = 'searchRegistration.requestId';
    abyssForm.setValue('searchRegistration.billingTin', billingTin);
    abyssForm.setValue(model, requestId);

    abyssForm.setValue('searchRegistration.billingTin', billingTin);
    abyssForm.setValue('searchRegistration.requestId', requestId);
  };

  const populateW9 = (w9Details) => {
    if (w9Details.fileName && w9Details.doc360Id) {
      const filename = { name: w9Details.fileName, size: 1 };
      setDocumentId({ id: 'w9form.fileName', value: w9Details.doc360Id });
      abyssForm.setValue('w9form.fileName', filename, { touched: false });
    }

    abyssForm.setValue(
      'w9form.federalTaxClassification',
      w9Details.federalTaxClassification
    );
    abyssForm.setValue('w9form.businessName', w9Details.businessName);
    abyssForm.setValue('w9form.name', w9Details.name);
  };

  const populatePracticeData = (tinDetails, practiceInformation) => {
    let model = 'practiceInformation.registrationTypeSelection';
    abyssForm.setValue(model, practiceInformation.registrationType);
    const pType = practiceInformation.practiceType;
    abyssForm.setValue('practiceInformation.practiceTypeValue', pType);

    model = 'practiceInformation.practiceTypeSelection';
    abyssForm.setValue(model, pType);

    model = 'practiceInformation.restrictionSelection';
    if (practiceInformation.isRevoked) {
      abyssForm.setValue(model, 'Yes');
    } else if (tinDetails.address && tinDetails.address.length > 0) {
      abyssForm.setValue(model, 'No');
    }

    if (practiceInformation.additionalDetails) {
      model = 'practiceInformation.restrictionInput';
      abyssForm.setValue(model, practiceInformation.additionalDetails);
    }

    if (practiceInformation.npi.length > 1) {
      let inputStr = '';
      practiceInformation.npi.forEach((val) => {
        inputStr = `${inputStr + val},`;
      });
      inputStr = inputStr.substring(0, inputStr.length - 1);
      model = 'practiceInformation.npiInformation';
      abyssForm.setValue(model, inputStr);
    } else {
      model = 'practiceInformation.npiInformation';
      abyssForm.setValue(model, practiceInformation.npi[0]);
    }

    model = 'practiceInformation.cliaFile';
    if (practiceInformation.fileName && practiceInformation.doc360IdCms) {
      const filename = { name: practiceInformation.fileName };
      abyssForm.setValue(model, filename);
      setDocumentId({ id: model, value: practiceInformation.doc360IdCms });
    }

    model = 'practiceInformation.cliaIdentification';
    abyssForm.setValue(model, practiceInformation.cliaId);

    model = 'practiceInformation.medicareId';
    abyssForm.setValue(model, practiceInformation.medicareId);

    model = 'practiceInformation.isNpiValid';
    abyssForm.setValue(model, practiceInformation.isNpiValid);

    model = 'practiceInformation.medLicense';
    abyssForm.setValue(model, practiceInformation.licenses);

    if (practiceInformation.licenses) {
      practiceInformation.licenses.forEach((val, index) => {
        model = `practiceInformation.medLicense[${index}].licenseType`;
        abyssForm.setValue(model, val.licenseType);
        model = `practiceInformation.medLicense[${index}].licenseNumber`;
        abyssForm.setValue(model, val.licenseNumber);
        model = `practiceInformation.medLicense[${index}].state`;
        abyssForm.setValue(model, val.state);
      });
    }
  };

  const populateLocation = (locationInfo) => {
    let model = '';
    let type = '';
    let doc360Ext = 'Proof';
    const regex = new RegExp('(?:SecondaryService).');

    locationInfo.forEach((val) => {
      const filename = { name: val.fileName, size: 1 };

      if (val.type === 'PrimaryService') {
        type = 'primaryServicing';
      }
      if (val.type === 'PrimaryBilling') {
        type = 'primaryBilling';
      }
      if (val.type === 'PrimaryRemit') {
        type = 'primaryRemittance';
      }
      if (regex.test(val.type)) {
        const index = val.type.substring(16, 17) - 1;
        type = `secondaryServicing[${index}]`;
        doc360Ext = '.proof';
      }
      model = `address.${type}.streetLine1`;

      abyssForm.setValue(model, val.streetLine1);
      model = `address.${type}.streetLine2`;
      abyssForm.setValue(model, val.streetLine2);
      model = `address.${type}.city`;
      abyssForm.setValue(model, val.city);
      model = `address.${type}.state`;
      abyssForm.setValue(model, val.state);
      model = `address.${type}.zipCode`;
      abyssForm.setValue(model, val.zipCode);

      if (val.doc360Id && val.fileName) {
        model = `address.${type}${doc360Ext}`;
        abyssForm.setValue(model, filename);
        setDocumentId({ id: model, value: val.doc360Id });
      }
    });
  };

  const populateAttestation = (attestFields) => {
    let model = 'attestation.submitterName';
    abyssForm.setValue(model, attestFields.name);
    model = 'attestation.jobTitle';
    if (attestFields.jobTitle !== null) {
      abyssForm.setValue(model, attestFields.jobTitle);
    }
    model = 'attestation.phoneNumber';
    abyssForm.setValue(model, attestFields.phoneNumber);
    model = 'attestation.emailAddress';
    abyssForm.setValue(model, attestFields.email);
    model = 'attestation.commPreference';
    abyssForm.setValue(model, attestFields.communicationPreference);
    model = 'attestation.claimDate';
    abyssForm.setValue(model, attestFields.claimDate);
    model = 'attestation.submitClaim';
    if (attestFields.isClaimSubmitted) {
      abyssForm.setValue(model, 'Yes');
    } else {
      abyssForm.setValue(model, 'No');
    }
  };

  const onYes = (tinDetails) => {
    setTrackingNumber(tinDetails.trackingNumber);
    if (tinDetails && tinDetails.billingTin) {
      populateLandingPage(tinDetails.billingTin, tinDetails.requestId);
    }
    if (tinDetails && tinDetails.w9Details) {
      populateW9(tinDetails.w9Details);
    }
    if (tinDetails && tinDetails.practiceInformation) {
      populatePracticeData(tinDetails, tinDetails.practiceInformation);
    }
    if (tinDetails && tinDetails.address && tinDetails.address.length > 0) {
      populateLocation(tinDetails.address);
    }
    if (tinDetails && tinDetails.attestationDetails) {
      populateAttestation(tinDetails.attestationDetails);
    }

    toggleReturn(true);

    const eventInteraction = 'jobStartSavedTinRegistration';
    const jobName = 'tin registration';
    const registrationContext = 'already saved registration';
    const providerBillingTaxID = tinDetails.billingTin;
    adobeTaggingEvent('SAVED_REGISTRATION', {
      eventInteraction,
      jobName,
      registrationContext,
      providerBillingTaxID,
    });

    abyssForm.validate('searchRegistration', (values) => {
      router.navigate('tin-registration/step/1');
    });
  };

  const populateDetails = (tinDrafts) => {
    setTrackingNumber(tinDrafts.trackingNumber);
    if (tinDrafts && tinDrafts.billingTin) {
      populateLandingPage(tinDrafts.billingTin, tinDrafts.requestId);
    }
    if (tinDrafts && tinDrafts.w9Details) {
      populateW9(tinDrafts.w9Details);
    }
    if (tinDrafts && tinDrafts.practiceInformation) {
      populatePracticeData(tinDrafts, tinDrafts.practiceInformation);
    }
    if (tinDrafts && tinDrafts.address && tinDrafts.address.length > 0) {
      populateLocation(tinDrafts.address);
    }
    if (tinDrafts && tinDrafts.attestationDetails) {
      populateAttestation(tinDrafts.attestationDetails);
    }

    toggleReturn(true);

    const eventInteraction = 'jobStartSavedTinRegistration';
    const jobName = 'tin registration';
    const registrationContext = 'already saved registration';
    const providerBillingTaxID = tinDrafts.billingTin;
    adobeTaggingEvent('SAVED_REGISTRATION', {
      eventInteraction,
      jobName,
      registrationContext,
      providerBillingTaxID,
    });

    abyssForm.validate('searchRegistration', (value) => {
      router.navigate('tin-registration/step/1');
    });
  };
  const closeUserBlockedModel = () => {
    setIsUserBlocked(false);
  };

  const closeTermsOfUseModel = () => {
    updateTerms(
      {
        uuid,
        accepted: true,
        domain: 'EPV-Portal',
      },
      (response) => {
        setTermsOfUse(false);
      },
      (error) => {
        setTermsOfUse(true);
      }
    );
  };

  const onLogout = () => {
    window.location = `${config('IDENTITY_API_URL')}/logout`;
  };

  const handleLogout = async () => {
    onLogout();
  };

  const events = {
    onSignout: () => {
      if (config('APP_ENV') !== 'local') {
        handleLogout();
      }
    },
  };

  return (
    <PageLayout>
      <Loading isLoading={isSuperUser ? false : retrieveDraftSaga.isLoading}>
        <PatientName id="dashboard-name">Hello, {firstName}</PatientName>
        <Heading>
          Start here to register your Taxpayer Identification Number (TIN) with
          UnitedHealthcare.
        </Heading>
        {(isAlertBanner || isTinRegError || isTinRegStatusError) && (
          <React.Fragment>
            <AlertDisplay>
              <Alert
                title={
                  isTinRegStatusError
                    ? 'We were unable to retrieve your TIN registration'
                    : 'Submission Error'
                }
              >
                {getMessage()}
              </Alert>
            </AlertDisplay>
          </React.Fragment>
        )}

        {isTermsOfUse && (
          <TermsOfUseModel
            isOpen={isTermsOfUse}
            closeModal={closeTermsOfUseModel}
          />
        )}

        {isUserBlocked && (
          <UserBlockedModal
            isOpen={isUserBlocked}
            closeModal={closeUserBlockedModel}
            events={events}
            timeRemaining={timeRemaining}
          />
        )}

        <Grid>
          <Grid.Col span={{ xs: '100%', md: '50%' }}>
            <TinRegistration
              setErrorRes={setErrorRes}
              errorRes={errorRes}
              setIsAlertBanner={setIsAlertBanner}
              setTinRegError={setTinRegError}
              setTinRegStatusError={setTinRegStatusError}
              onYes={onYes}
              populateDetails={populateDetails}
              setIsUserBlocked={setIsUserBlocked}
              setTimeRemaining={setTimeRemaining}
            />
          </Grid.Col>
          <Grid.Col span={{ xs: '100%', md: '50%' }}>
            <TinRegistrationStatus
              setIsAlertBanner={setIsAlertBanner}
              setTinRegStatusError={setTinRegStatusError}
              setTinRegError={setTinRegError}
            />
          </Grid.Col>
        </Grid>

        {isSessionDraft && (
          <SavedRegistration
            tinDraftResult={tinDraft}
            setTinDraft={setTinDraft}
            searchFilter={searchFilter}
            getFormatedDate={getFormatedDate}
            onYes={onYes}
            setSessionDraft={setSessionDraft}
          />
        )}
      </Loading>
    </PageLayout>
  );
};
